import { useEffect, useRef, useState } from 'react'
import shortid from 'shortid'

import { useAppSelector } from 'redux/hooks'

import { CardType, defaultCard, Finishes } from 'types/deck'

import CardAutocomplete from 'components/formElements/CardAutocomplete'
import AsyncEditionDropdown from 'components/cardSearchPanel/searchFormElements/asyncEditionDropdown'
import Input from 'components/formElements/SemanticInput'
import Select from 'components/formElements/SemanticSelect'
import Modal from 'components/elements/Modal'
import PhatButton from 'components/formElements/PhatButton'
import Checkbox from 'components/formElements/Checkbox'

import styles from './detailedAddModal.module.scss'

type Props = {
  open: boolean
  onClose: () => void
  onAddCard: (cardToAdd: CardType) => void
}

const DetailedAddModal = ({ open, onClose, onAddCard }: Props) => {
  // Ref passed as such due to the way the child is implementd as a ref callback
  // TODO - would love to change the child here
  let ref = useRef<any>(null)

  const [query, setQuery] = useState('')
  const [selectedCard, setSelectedCard] = useState<null | CardType>(null)
  const [leaveModalOpen, setLeaveModalOpen] = useState(false)

  const categories = useAppSelector(state => state.deck.categories)
  const colorLabels = useAppSelector(state => state.deck.colorLabels)

  const handleCardSelected = (card: CardType) => {
    setSelectedCard({ ...card, qty: 1 })
    setQuery(card.name)
  }

  const handleAddCard = (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCard) return

    onAddCard(selectedCard)

    setSelectedCard(null)
    setQuery('')

    if (leaveModalOpen) ref?.current.focus()
    else onClose()
  }

  useEffect(() => {
    if (open) setTimeout(() => ref?.current.focus(), 100)
  }, [open])

  const disableRemainingElements = !selectedCard
  const displayCard = selectedCard || defaultCard

  const categoryOptions = Object.values(categories)
    .sort((a, b) => (a.name < b.name ? -1 : 1))
    .map(c => ({ text: c.name, value: c.name }))

  const finishOptions = displayCard.options.map(option => ({
    text: option === 'Normal' ? 'Nonfoil' : option,
    value: option,
  }))

  const colorLabelOptions = colorLabels.map(c => ({
    text: (
      <span className={styles.colorLabel}>
        <span className={styles.splotch} style={{ backgroundColor: c.color }} />
        {c.name || 'Default color tag'}
      </span>
    ),
    value: c.name || 'Default color tag',
  }))

  return (
    <Modal open={open} onClose={onClose} closeButton modalClassName={styles.modal}>
      <h4>Detailed Add</h4>
      <form className={styles.content} data-lpignore="true" autoComplete="off">
        <CardAutocomplete
          fetchCard
          ref={ref}
          value={query}
          onChange={(_e: any, { value }: any) => setQuery(value)}
          onSelect={handleCardSelected}
          icon="add"
          placeholder="Card Name"
          className={styles.autocomplete}
        />
        <Input
          type="number"
          disabled={disableRemainingElements}
          placeholder="Quantity"
          value={selectedCard?.qty}
          onChange={qty => setSelectedCard({ ...displayCard, qty: Number(qty) })}
        />
        <AsyncEditionDropdown
          key={`editions-${selectedCard?.id || shortid.generate()}`}
          disabled={disableRemainingElements}
          card={displayCard}
          onCardSelected={setSelectedCard}
          triggerClassName={styles.editionsDropdown}
        />
        <Select
          key={`categories-${selectedCard?.id}`}
          containerClassName={styles.select}
          placeholder="Default Category"
          name="category"
          fluid
          search
          selection
          multiple
          options={categoryOptions}
          disabled={disableRemainingElements}
          onChange={categories => setSelectedCard({ ...displayCard, categories })}
        />
        <Select
          fluid
          containerClassName={styles.select}
          placeholder="Nonfoil"
          name="modifier"
          options={finishOptions}
          disabled={disableRemainingElements}
          value={displayCard.modifier}
          onChange={(modifier: Finishes) => setSelectedCard({ ...displayCard, modifier })}
        />
        <Select
          fluid
          containerClassName={styles.select}
          placeholder="None"
          name="colorLabel"
          options={colorLabelOptions}
          disabled={disableRemainingElements}
          value={displayCard.colorLabel.name || 'Default color tag'}
          onChange={(colorLabelName: string) =>
            setSelectedCard({
              ...displayCard,
              colorLabel: colorLabels.find(c => c.name === colorLabelName) || colorLabels[0],
            })
          }
        />
        <PhatButton type="submit" disabled={disableRemainingElements} color="green" onClick={handleAddCard}>
          Add card to deck
        </PhatButton>
        <Checkbox
          className={styles.checkbox}
          checked={leaveModalOpen}
          onChange={() => setLeaveModalOpen(!leaveModalOpen)}
          label="Add another card (leaves modal open)"
        />
      </form>
    </Modal>
  )
}

export default DetailedAddModal
